import type { Dir, InputFrame, KitKind, SimConfig, SimEvent, SimMode, SimState } from './types.ts';
import { NO_INPUT, createSim, step } from './sim.ts';
import { hashState } from './hash.ts';

/** Input bitmask: bits 0-2 direction (0-4), bit 3 stop, bit 4 fire, bit 5 skip (pointerdown), bit 6 beam. */
export function packInput(i: InputFrame): number {
  return (i.dir & 7) | (i.stop ? 8 : 0) | (i.fire ? 16 : 0) | (i.skip ? 32 : 0) | (i.beam ? 64 : 0);
}
export function unpackInput(m: number): InputFrame {
  return { dir: (m & 7) as Dir, stop: (m & 8) !== 0, fire: (m & 16) !== 0, skip: (m & 32) !== 0, beam: (m & 64) !== 0 };
}

export interface Recording {
  version: 1;
  mode: SimMode;
  seed: number;
  boardIds: string[];
  timeLimitTicks?: number;
  infiniteLives?: boolean;
  playerTicksPerCellH?: number;
  playerTicksPerCellV?: number;
  picks?: KitKind[];
  /** Sparse list of [tick, packedInput] for ticks with non-empty input. */
  inputs: [number, number][];
  ticks: number;
  finalHash: string;
  finalScore: number;
}

export class Recorder {
  readonly inputs: [number, number][] = [];
  private readonly config: SimConfig;
  constructor(config: SimConfig) {
    this.config = config;
  }
  record(tick: number, input: InputFrame): void {
    const m = packInput(input);
    if (m !== 0) this.inputs.push([tick, m]);
  }
  finish(state: SimState): Recording {
    const c = this.config;
    return {
      version: 1,
      mode: c.mode,
      seed: c.seed,
      boardIds: c.boards.map((b) => b.id),
      timeLimitTicks: c.timeLimitTicks,
      infiniteLives: c.infiniteLives,
      playerTicksPerCellH: c.playerTicksPerCellH,
      playerTicksPerCellV: c.playerTicksPerCellV,
      picks: c.picks,
      inputs: this.inputs.slice(),
      ticks: state.tick,
      finalHash: hashState(state),
      finalScore: state.score,
    };
  }
}

export function configFromRecording(rec: Recording, boardsById: Map<string, SimConfig['boards'][number]>): SimConfig {
  const boards = rec.boardIds.map((id) => {
    const b = boardsById.get(id);
    if (!b) throw new Error(`Recording references unknown board '${id}'`);
    return b;
  });
  return {
    mode: rec.mode,
    seed: rec.seed,
    boards,
    timeLimitTicks: rec.timeLimitTicks,
    infiniteLives: rec.infiniteLives,
    playerTicksPerCellH: rec.playerTicksPerCellH,
    playerTicksPerCellV: rec.playerTicksPerCellV,
    picks: rec.picks,
  };
}

/** Headless replay: run the recorded inputs and return the final state + hash. */
export function replay(
  rec: Recording,
  boardsById: Map<string, SimConfig['boards'][number]>,
  onTick?: (state: SimState, events: SimEvent[]) => void,
): { state: SimState; hash: string; matches: boolean } {
  const config = configFromRecording(rec, boardsById);
  const state = createSim(config);
  let ii = 0;
  for (let t = 1; t <= rec.ticks; t++) {
    let input: InputFrame = NO_INPUT;
    if (ii < rec.inputs.length && rec.inputs[ii][0] === t) {
      input = unpackInput(rec.inputs[ii][1]);
      ii++;
    }
    const events = step(state, input, config);
    onTick?.(state, events);
  }
  const hash = hashState(state);
  return { state, hash, matches: hash === rec.finalHash };
}
